'use client';

import Image from 'next/image';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import XIcon from '@mui/icons-material/X';
import { FOOTER } from '@/constants/footer';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import BrandDefinition from '@/types/BrandDefinition';
import FooterDictionary from '@/types/dictionaries/Footer';
import { Socials } from '@/types/Socials';
import PartnerLogos from '@/components/Footer/components/PartnerLogos';
import { PartnerLogoData } from '@/types/PartnerLogoData';
import ExternalLinksSection from './components/ExternalLinksSection';
import InformationSection from './components/InformationSection';
import ServiceHotlineSection from './components/ServiceHotlineSection';
import Accordion from '../Accordion';
import MyAccountSection from './components/MyAccountSection';

const FooterContent = (isMobileOrTablet: boolean, year: number, dict: FooterDictionary, socials: Socials) => {
	const showSocialLinks = socials.facebook || socials.linkedin || socials.x || socials.youtube;

	return (
		<>
			<Divider
				sx={{
					width: isMobileOrTablet ? 'auto' : '100%',
					padding: isMobileOrTablet ? '0px' : '24px',
					height: '1px',
					borderColor: 'grey.dark',
				}}
			/>
			<Box
				sx={{
					width: isMobileOrTablet ? 'auto' : '100%',
					display: 'flex',
					alignItems: 'center',
					justifyContent: isMobileOrTablet ? 'center' : 'space-between',
					gap: isMobileOrTablet ? '32px' : '0px',
					alignSelf: isMobileOrTablet ? 'stretch' : 'auto',
					padding: isMobileOrTablet ? '32px 20px 24px 20px' : '24px 0px 0px 0px',
					flex: isMobileOrTablet ? 'none' : '1 0 0',
				}}
			>
				<Image
					width={isMobileOrTablet ? 147 : 184}
					height={isMobileOrTablet ? 32 : 40}
					src="/logo_IR_white.svg"
					alt="IR logo"
				/>
				<Typography variant="h7">
					©{year}. {dict.ingersollTrademark}
				</Typography>
				{!isMobileOrTablet && showSocialLinks && (
					<Box>
						{socials.facebook && (
							<IconButton
								href={socials.facebook}
								target="_blank"
								rel="noopener noreferrer"
								sx={{ display: socials.facebook ? 'inline' : 'none' }}
							>
								<FacebookIcon sx={{ color: 'white.main' }} />
							</IconButton>
						)}
						{socials.linkedin && (
							<IconButton
								href={socials.linkedin}
								target="_blank"
								rel="noopener noreferrer"
								sx={{ display: socials.linkedin ? 'inline' : 'none' }}
							>
								<LinkedInIcon sx={{ color: 'white.main' }} />
							</IconButton>
						)}
						{socials.x && (
							<IconButton
								href={socials.x}
								target="_blank"
								rel="noopener noreferrer"
								sx={{ display: socials.x ? 'inline' : 'none' }}
							>
								<XIcon sx={{ color: 'white.main' }} />
							</IconButton>
						)}
						{socials.youtube && (
							<IconButton
								href={socials.youtube}
								target="_blank"
								rel="noopener noreferrer"
								sx={{ display: socials.youtube ? 'inline' : 'none' }}
							>
								<YouTubeIcon sx={{ color: 'white.main' }} />
							</IconButton>
						)}
						{socials.instagram && (
							<IconButton
								href={socials.instagram}
								target="_blank"
								rel="noopener noreferrer"
								sx={{ display: socials.instagram ? 'inline' : 'none' }}
							>
								<InstagramIcon sx={{ color: 'white.main' }} />
							</IconButton>
						)}
					</Box>
				)}
			</Box>
		</>
	);
};

const DesktopView = (
	isMobileOrTablet: boolean,
	brand: BrandDefinition,
	year: number,
	dict: FooterDictionary,
	socials: Socials,
	partnerLogos: PartnerLogoData[],
	showMyAccountSection: boolean,
) => (
	<Container
		maxWidth="xl"
		sx={{
			display: 'flex',
			padding: '24px 60px',
			flexDirection: 'column',
			justifyContent: 'center',
			alignItems: 'center',
			alignSelf: 'stretch',
		}}
	>
		<Box
			sx={{
				width: '100%',
				display: 'flex',
				alignItems: 'flex-start',
				justifyContent: 'space-between',
				flexWrap: 'wrap',
				gap: '32px',
			}}
		>
			<ServiceHotlineSection dict={dict} brand={brand} showHeading={!isMobileOrTablet} />
			<InformationSection dict={dict} brand={brand} showHeading={!isMobileOrTablet} />
			{!showMyAccountSection && <MyAccountSection dict={dict} brand={brand} showHeading={!isMobileOrTablet} />}
			<ExternalLinksSection dict={dict} brand={brand} showHeading={!isMobileOrTablet} />
			{partnerLogos.length > 0 && <PartnerLogos partnerLogoData={partnerLogos} />}
		</Box>
		{FooterContent(isMobileOrTablet, year, dict, socials)}
	</Container>
);

const MobileOrTabletView = (
	isMobileOrTablet: boolean,
	brand: BrandDefinition,
	year: number,
	dict: FooterDictionary,
	socials: Socials,
	partnerLogos: PartnerLogoData[],
	showMyAccountSection: boolean,
) => (
	<>
		<Accordion
			titles={{
				serviceHotline: dict.serviceHotlineSection.title,
				information: dict.informationSection.title,
				externalLinks: dict.externalLinksSection.title,
				...(!showMyAccountSection && { MyAccountSection: dict.accountSection.title }),
			}}
			contents={{
				serviceHotline: <ServiceHotlineSection dict={dict} brand={brand} showHeading={!isMobileOrTablet} />,
				information: <InformationSection dict={dict} brand={brand} showHeading={!isMobileOrTablet} />,
				externalLinks: <ExternalLinksSection dict={dict} brand={brand} showHeading={!isMobileOrTablet} />,
				...(!showMyAccountSection && {
					MyAccountSection: <MyAccountSection dict={dict} brand={brand} showHeading={!isMobileOrTablet} />,
				}),
			}}
			color="white.main"
			backgroundColor="grey.ultraDark"
			expandedColor="white.main"
		/>
		{partnerLogos.length > 0 && (
			<Box sx={{ mt: '32px', mb: '28px' }}>
				<PartnerLogos partnerLogoData={partnerLogos} />
			</Box>
		)}
		{FooterContent(isMobileOrTablet, year, dict, socials)}
	</>
);

interface FooterProps {
	dict: FooterDictionary;
	brand: BrandDefinition;
	showMyAccountSection: boolean;
}

export default function Footer({ dict, brand, showMyAccountSection }: Readonly<FooterProps>) {
	const theme = useTheme();
	const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
	const year = new Date().getFullYear();
	const footerConstants = FOOTER[brand.brandCode];

	return (
		<>
			<Divider sx={{ border: '2px solid', borderColor: 'red.main', height: '2px' }} />
			<Box component="footer" py={3} bgcolor="grey.ultraDark" color="white.main">
				{isMobileOrTablet
					? MobileOrTabletView(
							isMobileOrTablet,
							brand,
							year,
							dict,
							footerConstants.socials,
							footerConstants.partnerLogos,
							showMyAccountSection,
						)
					: DesktopView(
							isMobileOrTablet,
							brand,
							year,
							dict,
							footerConstants.socials,
							footerConstants.partnerLogos,
							showMyAccountSection,
						)}
			</Box>
		</>
	);
}
